import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, Table, Button, Spinner, Tab, Tabs } from "react-bootstrap";
import { horizon } from "../api/horizon";
import { usePageMetadata } from "../context/PageMetadataContext";
import CopyIcon from "../components/CopyIcon";
import OperationText from "../components/OperationText";
import { CardContent } from "../components/Card";
import { okx } from "../api/okx";
import { timelib } from "../utils/time";
import AccountLabel from "../components/AccountLabel";
import { describePredicate, formatTime, getUnlockTime } from "../utils/predicate";
import { socialchain } from "../api/socialchain";
import { useLanguage } from "../context/LanguageContext";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";
import { LabledAddress } from "../utils/addresss";

const AccountDetail = () => {
  const { address } = useParams<{ address: string }>();
  const { language, t } = useLanguage();
  const [accountData, setAccountData] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [operations, setOperations] = useState<any[]>([]);
  const [effects, setEffects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  //
  const [txNextLink, setTxNextLink] = useState("");
  const [txPrevLink, setTxPrevLink] = useState("");
  const [txPage, setTxPage] = useState(1);

  const [opNextLink, setOpNextLink] = useState("");
  const [opPrevLink, setOpPrevLink] = useState("");
  const [opPage, setOpPage] = useState(1);

  const [efNextLink, setEfNextLink] = useState("");
  const [efPrevLink, setEfPrevLink] = useState("");
  const [efPage, setEfPage] = useState(1);
  //
  const [piPriceUSD, setPiPriceUSD] = useState(0.0);
  //
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  //
  const [claimableBalance, setClaimableBalance] = useState<any>();
  const [totalLocking, setTotalLocking] = useState(0);
  const [lockingItemCount, setLockingItemCount] = useState(0);
  const [totalClaimable, setTotalClaimable] = useState(0);
  const [nextUnlockTime, setNextUnlockTime] = useState("");
  const [claimableItemCount, setClaimableItemCount] = useState(0);
  const [isScamWarning, setIsScamWarning] = useState(false);

  useEffect(() => {
    const translatedTitle = t('account.title').replace('{{address}}', address || '');
    const translatedHeading = t('account.heading_with_address').replace('{{address}}', address || '');
    const translatedDescription = t('account.description').replace('{{address}}', address || '');
    
    setTitle(translatedTitle);
    setHeading(translatedHeading);
    setDescription(translatedDescription);
    
    // Update metadata immediately for SEO
    document.title = translatedTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", translatedDescription);
  }, [address, setTitle, setDescription, setHeading, language, t]);

  const formatAmount = (amount: number): string => {
    return amount.toLocaleString(language, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2 
    });
  };

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString(language, { 
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2 
    });
  };

  const fetchPiPrice = async () => {
    try {
      okx.getMarketData().then(marketData => {
        // Market Stats
        setPiPriceUSD(parseFloat(marketData.idxPx));
      });
    } catch (error) {
      console.error("Error fetching Pi price:", error);
    }
  };

  useEffect(() => {
    fetchPiPrice();
  }, []);

  // Fetch Account detail from API
  const fetchAccountDetail = async () => {
    try {
      setLoading(true);
      if (address) {
        const response = await horizon.getAccountDetails(address);
        setAccountData(response);
        const translatedTitle = t('account.title')+address;
        setTitle(translatedTitle);
        setHeading(t('account.heading_with_address')+ address );
        setDescription(t('account.description')+ address );
      } else {
        setAccountData(null);
      }
    } catch (error) {
      console.error("Error fetching Account detail:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch paginated tab data
  const fetchTransactions = async (Link = "", page = 1) => {
    try {
      setTabLoading(true);
      const response = await horizon.getTransactions("", Link!=""?Link:"https://api.mainnet.minepi.com/accounts/"+address+"/transactions?limit=10&order=desc");
      //if order asc, reverse the array, and next is prev and prev is next
      if (Link==""||Link.indexOf("order=desc") > 0) {
        setTransactions(response._embedded.records);
        setTxNextLink(response._links.next.href);
        setTxPrevLink(response._links.prev.href);
        setTxPage(page);
      }
      else{
        setTransactions(response._embedded.records.reverse());
        setTxNextLink(response._links.prev.href);
        setTxPrevLink(response._links.next.href);
        setTxPage(page);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchOperations = async (Link = "", page = 1) => {
    try {
      setTabLoading(true);
      const response = await horizon.getOperations("", Link!=""?Link:"https://api.mainnet.minepi.com/accounts/"+address+"/operations?limit=10&order=desc");
       //if order asc, reverse the array, and next is prev and prev is next
       if (Link==""||Link.indexOf("order=desc") > 0) {
      setOperations(response._embedded.records);
      setOpNextLink(response._links.next.href);
      setOpPrevLink(response._links.prev.href);
      setOpPage(page);
    }
    else{
      setOperations(response._embedded.records.reverse());
      setOpNextLink(response._links.prev.href);
      setOpPrevLink(response._links.next.href);
      setOpPage(page);
    }
    } catch (error) {
      console.error("Error fetching operations:", error);
    } finally {
      setTabLoading(false);
    }
  };

  const fetchEffects = async (Link = "", page = 1) => {
    try {
      setTabLoading(true);
      const response = await horizon.getEffects(address||"", Link!=""?Link:"https://api.mainnet.minepi.com/accounts/"+address+"/effects?limit=10&order=desc");
       //if order asc, reverse the array, and next is prev and prev is next
       if (Link==""||Link.indexOf("order=desc") > 0) {
      setEffects(response._embedded.records);
      setEfNextLink(response._links.next.href);
      setEfPrevLink(response._links.prev.href);
      setEfPage(page);
    }
    else{
      setEffects(response._embedded.records.reverse());
      setEfNextLink(response._links.prev.href);
      setEfPrevLink(response._links.next.href);
      setEfPage(page);
    }
    } catch (error) {
      console.error("Error fetching effects:", error);
    } finally {
      setTabLoading(false);
    }
  };
  const fecthClaimableBalance = () => {
    try {
      horizon.getClaimableBalances(address||"").then(response=>{
        setClaimableBalance(response._embedded.records);
        //
        var locking=0;
        var lockingCount=0;
        var claimable=0;
        var nextUnlock=0;
        var claimableCount=0;
        for (let i = 0; i < response._embedded.records.length; i++) {
          const element = response._embedded.records[i];
          const claimt=element.claimants.find((c: any) => c.destination == address);
          if (claimt) {
            const unlockTime=getUnlockTime(claimt.predicate,element.last_modified_time);
            if (unlockTime>0) {
              locking+=parseFloat(element.amount);
              lockingCount++;
              if (nextUnlock==0||nextUnlock>unlockTime) {
                nextUnlock=unlockTime;
              }
            }
            else{
              claimable+=parseFloat(element.amount);
              claimableCount++;
            }
          }
        }
        setTotalLocking(locking);
        setTotalClaimable(claimable);
        setLockingItemCount(lockingCount);
        setClaimableItemCount(claimableCount);
        if (nextUnlock>0) {
          const unlockDate=new Date(Date.now() + Math.round(nextUnlock)*1000).toUTCString();
          setNextUnlockTime(unlockDate);
        }
      })
    } catch (error) {
      console.error("Error fetching claimable balance:", error);
    }
  };
    // Fetch transactions, operations, effects
    const fetchTabData = async () => {
      try {
        setTabLoading(true);
        if (address) {
         fetchTransactions("",1);
         fetchOperations("",1);
         fetchEffects("",1);
        }
      } catch (error) {
        console.error("Error fetching tab data:", error);
      } finally {
        setTabLoading(false);
      }
    };
    const fecthScamWarning = async () => {
      try {
        socialchain.checkScamWarning(address || "").then(response=>{
          if (response) {
            setIsScamWarning(true);
          }else{
            setIsScamWarning(false);
          }
        }
        )
      } catch (error) {
        console.error("Error fetching scam warning:", error);
      }
    };
    useEffect(() => {
      fecthScamWarning();
      fetchAccountDetail();
      fetchTabData();
      fecthClaimableBalance();
    }, [address]);

    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p>{t('account.loading')}</p>
        </div>
      );
    }

    if (!accountData) {
      return <p className="text-center text-danger">{t('account.not_found')}</p>;
    }

    return (
      <div className="">
        <h2 className="address-title long-text">
          {address} <CopyIcon textToCopy={address || ""} />
        {" "}
        {address!=null&&LabledAddress[address]!=null ? 
        <span className="badge bg-success text-light mb-3">
                        {LabledAddress[address].Logo?<><img src={LabledAddress[address].Logo} alt={LabledAddress[address].Name} className="cex-logo" width={16} /> {" "}</>:""}
                        {LabledAddress[address].Url?<a target="_blank" className="text-light" href={LabledAddress[address].Url}>{LabledAddress[address].Name}</a>:LabledAddress[address].Name} 
                    </span>:""}
                    </h2>
        {isScamWarning && (
          <p>
            <span className="badge bg-danger">{t('account.scam_warning_title')}</span>{" "}
            {t('account.scam_warning_description')}
          </p>
        )}
        <div className="row g-3 mb-4">
          {/* Market Stats */}
          <div className="col-md-4 text-color-normal">
            <Card>
              <CardContent>
                <h5 className="text-color-normal">{t('account.overview')}</h5>
                <div className="d-flex justify-content-between">
                  <div className="text-color-normal">
                    {t('account.available_balance')}:
                  </div>
                  <div>
                    <span className="strong-number">
                      {formatAmount(parseFloat(accountData.balances[0].balance))}π
                    </span>{" "}
                    <span className="text-muted">
                      ({formatCurrency(parseFloat(accountData.balances[0].balance) * piPriceUSD)})
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="text-color-normal">
                    {t('account.last_modified_time')}:
                  </div>
                  <div>
                    <span className="text-muted">{timelib.timeFormat(accountData.last_modified_time, language)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-md-4">
            <Card>
              <CardContent>
                <h5 className="text-color-normal">{t('account.locking')}</h5>
                <div className="d-flex justify-content-between">
                  <div className="text-color-normal">
                    {t('account.total_locking').replace('{{count}}', lockingItemCount.toString())}:
                  </div>
                  <div>
                    <span className="strong-number">{formatAmount(totalLocking)}π</span>{" "}
                    <span className="text-muted">
                      ({formatCurrency(totalLocking * piPriceUSD)})
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="text-color-normal">
                    {t('account.next_unlock_time')}:
                  </div>
                  <div>
                    <span className="text-color-normal">
                      {nextUnlockTime !== "" ? timelib.timeFormat(nextUnlockTime, language) : ""}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-md-4">
            <Card>
              <CardContent>
                <h5 className="text-color-normal">{t('account.claimable')}</h5>
                <div className="d-flex justify-content-between">
                  <div className="text-color-normal">
                    {t('account.amount')}:
                  </div>
                  <div>
                    <span className="strong-number">{formatAmount(totalClaimable)}π</span>{" "}
                    <span className="text-muted">
                      ({formatCurrency(totalClaimable * piPriceUSD)})
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="text-color-normal">
                    {t('account.number_of_items')}:
                  </div>
                  <div>
                    <span className="text-color-normal">
                      {claimableItemCount == 100 
                        ? t('account.over_100_items') 
                        : formatAmount(claimableItemCount)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="row g-3 mb-4 d-block d-md-none">
        {/* <AdSenseUnit/> */}
        {/* <AdUnit
                  className="67cc9f95aa72d3d47fe128c2"
                  width="300px"
                  height="100px"
                  centered={true}
                /> */}
      </div>
        <div className="white-zone">
          {/* Tabs for Transactions, Operations, Effects */}
          <Tabs defaultActiveKey="operations" className="mt-4">
            <Tab eventKey="operations" title={t('account.tabs.operations')}>
              {tabLoading ? (
                <Spinner animation="border" variant="primary" className="mt-3" />
              ) : (
                <>
                  <Table hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>{t('operations.source_account')}</th>
                        <th>{t('operations.operation')}</th>
                        <th>{t('operations.amount')}</th>
                        <th>{t('operations.tx')}</th>
                        <th>{t('operations.time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operations.map((op) => (
                        <tr key={op.id}>
                          <td>
                            <AccountLabel account={op.source_account} shorten={true} />
                          </td>
                          <td><OperationText op={op} PiPrice={piPriceUSD} /></td>
                          <td style={{ fontWeight: "500" }}>
                            {op.amount ? formatAmount(parseFloat(op.amount)) : ""}
                          </td>
                          <td>
                            <Link to={`/tx/${op.transaction_hash}`} className="text-decoration-none">
                              {op.transaction_hash.slice(0, 16)}...
                            </Link>
                            {" "}
                            <CopyIcon textToCopy={op.transaction_hash} />
                          </td>
                          <td>{timelib.timeAgo(op.created_at, language)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => fetchOperations(opPrevLink, opPage - 1)}
                      disabled={!opPrevLink || tabLoading || opPage === 1}
                    >
                      {t('pagination.previous')}
                    </Button>
                    <span className="mx-3">{t('pagination.page')} {opPage}</span>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => fetchOperations(opNextLink, opPage + 1)}
                      disabled={!opNextLink || tabLoading}
                    >
                      {t('pagination.next')}
                    </Button>
                  </div>
                </>
              )}
            </Tab>
            <Tab eventKey="transactions" title={t('account.tabs.transactions')}>
              {tabLoading ? (
                <Spinner animation="border" variant="primary" className="mt-3" />
              ) : (
                <>
                  <Table hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>{t('account.tx_hash')}</th>
                        <th>{t('operations.source_account')}</th>
                        <th>{t('account.operation_count')}</th>
                        <th>{t('account.fee')}</th>
                        <th>{t('account.created_at')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td>
                            <Link to={`/tx/${tx.id}`} className="text-decoration-none">
                              {tx.id.slice(0, 16)}...
                            </Link>
                            {" "}
                            <CopyIcon textToCopy={tx.id} />
                          </td>
                          <td>
                            <AccountLabel account={tx.source_account} shorten={true} />
                          </td>
                          <td>{tx.operation_count}</td>
                          <td>{tx.fee_charged / 10_000_000} π</td>
                          <td>{timelib.timeAgo(tx.created_at, language)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => fetchTransactions(txPrevLink, txPage - 1)}
                      disabled={!txPrevLink || tabLoading || txPage === 1}
                    >
                      {t('pagination.previous')}
                    </Button>
                    <span className="mx-3">{t('pagination.page')} {txPage}</span>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => fetchTransactions(txNextLink, txPage + 1)}
                      disabled={!txNextLink || tabLoading}
                    >
                      {t('pagination.next')}
                    </Button>
                  </div>
                </>
              )}
            </Tab>
            <Tab eventKey="effects" title={t('account.tabs.effects')}>
              {tabLoading ? (
                <Spinner animation="border" variant="primary" className="mt-3" />
              ) : (
                <>
                  <Table hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>{t('account.account')}</th>
                        <th>{t('account.effect')}</th>
                        <th>{t('operations.amount')}</th>
                        <th>{t('operations.time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {effects.map((effect) => (
                        <tr key={effect.id}>
                          <td>
                            <Link to={`/account/${effect.account}`} className="text-decoration-none">
                              {effect.account.slice(0, 8)}...{effect.account.slice(-8)}
                            </Link>
                            {" "}
                            <CopyIcon textToCopy={effect.account} />
                          </td>
                          <td>{t(`effects.${effect.type}`) || effect.type.split("_").join(" ")}</td>
                          <td style={{ fontWeight: "500" }}>
                            <span className={effect.type == "account_credited" ? "text-success" : effect.type == "account_debited" ? "text-danger" : "text-muted"}>
                              {effect.type == "account_debited" ? "-" : effect.type == "account_credited" ? "+" : ""}
                              {effect.amount ? formatAmount(parseFloat(effect.amount)) : ""}
                            </span>
                          </td>
                          <td>{timelib.timeAgo(effect.created_at, language)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => fetchEffects(efPrevLink, efPage - 1)}
                      disabled={!efPrevLink || tabLoading || efPage === 1}
                    >
                      {t('pagination.previous')}
                    </Button>
                    <span className="mx-3">{t('pagination.page')} {efPage}</span>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => fetchEffects(efNextLink, efPage + 1)}
                      disabled={!efNextLink || tabLoading}
                    >
                      {t('pagination.next')}
                    </Button>
                  </div>
                </>
              )}
            </Tab>
            <Tab eventKey="claimables" title={t('account.tabs.claimable_balances')}>
              {tabLoading ? (
                <Spinner animation="border" variant="primary" className="mt-3" />
              ) : (
                <>
                  <div className="" style={{ marginTop: "10px" }}>{t('account.latest_100_claimable')}</div>
                  <Table hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>{t('account.sponsor')}</th>
                        <th>{t('operations.amount')}</th>
                        <th>{t('account.predicate')}</th>
                        <th>{t('account.create_time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claimableBalance?.map((claim: any) => (
                        <tr key={claim.id}>
                          <td>
                            <AccountLabel account={claim.sponsor} shorten={true} />
                          </td>
                          <td style={{ fontWeight: "500" }}>{formatAmount(parseFloat(claim.amount))}</td>
                          <td>{describePredicate(claim.claimants.find((c: any) => c.destination == address)?.predicate, language)}</td>
                          <td>{timelib.timeFormat(claim.last_modified_time, language)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </Tab>
          </Tabs>
        </div>
        
      </div>
    );
  };

  export default AccountDetail;
